import { useEffect, useMemo, useRef, useState } from 'react';
import { useApiService } from '../../../contexts/ApiServiceContext/ApiServiceContext';
import { RequestState, useAsyncOperationState } from '../../../dorian-shared/hooks/useAsyncOperationState';
import { BookLocation } from '../../../dorian-shared/types/bookLocation/bookLocation';
import { Branch } from '../../../dorian-shared/types/branch/Branch';
import { Character } from '../../../dorian-shared/types/character/Character';
import { logger } from '../../loggerService/loggerService';
import { BranchTextParser, ParserDictionaries } from '../parsers/BranchTextParser';
import { EpisodeTextImport } from '../models/EpisodeTextImport';

type ExpressionConfig = { id: number; title: string; value: string; def: boolean };
type MemoryDTO = { id: number; name: string; [key: string]: unknown };

function normalizeBookCharacters(characters: Character[] | null | undefined): Character[] {
  return (characters ?? [])
    .filter(Boolean)
    .map((character) => ({
      ...character,
      alias: character.alias || character.name || '',
      name: character.name || character.alias || '',
      lockArt: Boolean(character.lockArt),
      lockName: Boolean(character.lockName),
      hasForceAuthorsChoiceName: Boolean(character.hasForceAuthorsChoiceName),
      group: character.group ?? '',
      type: character.type ?? 'default',
      isPlayer: Boolean(character.isPlayer),
    }))
    .filter((character) => Boolean(character.alias));
}

function buildValidationLocationsFromBranches(branches: Branch[]): BookLocation[] {
  const seen = new Set<string>();
  const locations: BookLocation[] = [];
  for (const branch of branches) {
    if (branch.location?.alias && !seen.has(branch.location.alias)) {
      seen.add(branch.location.alias);
      locations.push({
        id: branch.location.id ?? locations.length + 1,
        title: branch.location.alias,
        isDefault: false,
        backgroundId: -1,
        alias: branch.location.alias,
        audioId: null,
        audioSettings: { volume: 100, loop: false },
        videoId: null,
        video: null,
        videoSettings: null,
        isPreviewable: true,
        branches_count: 0,
        stories_count: 0,
        image: { id: -1, alias: '', imageName: '', imageUrl: '' },
      });
    }
  }
  return locations;
}

const EMPTY_NODES: Branch[] = [];

export function useEpisodeNodes(
  episodeId: number,
  options: {
    enabled?: boolean;
    preloadedNodes?: Branch[];
    bookId?: number;
    expressions?: ExpressionConfig[];
    memoryBank?: MemoryDTO[];
    achievements?: { id: number | string; name: string; rewards?: { id?: number; name?: string }[] }[];
  } = {},
) {
  const {
    enabled = true, preloadedNodes = EMPTY_NODES, bookId,
    expressions, memoryBank, achievements,
  } = options;
  const apiService = useApiService();

  // F5: Stabilize preloadedNodes reference — use ref to avoid re-fetch on parent re-render
  const preloadedNodesRef = useRef(preloadedNodes);
  preloadedNodesRef.current = preloadedNodes;
  const [nodes, setNodes] = useState<Branch[]>([]);
  const [fetchedCharacters, setFetchedCharacters] = useState<Character[]>([]);
  const [validationCharacters, setValidationCharacters] = useState<Character[]>([]);
  const [validationLocations, setValidationLocations] = useState<BookLocation[]>([]);
  const [episodeTextImport, setEpisodeTextImport] = useState<EpisodeTextImport | null>(null);
  const [tsvText, setTsvText] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState('');
  const [charactersReady, setCharactersReady] = useState(false);
  const [isParsing, setIsParsing] = useState(false);
  const [
    requestState,
    {
      setToError,
      setToLoading,
      setToSuccess,
    },
  ] = useAsyncOperationState();

  const dictionaries = useMemo((): ParserDictionaries => ({
    characters: new Map((fetchedCharacters || []).map(c => [Number(c.id), c.alias || c.name || 'Character_unknown'])),
    expressions: new Map((expressions || []).map(e => [Number(e.id), e.value])),
    variables: new Map((memoryBank || []).map(m => [Number(m.id), m.name])),
    variableTypes: new Map((memoryBank || []).map(m => [Number(m.id), String((m as { type?: string }).type ?? '')])),
    branchNames: new Map((nodes || []).map(b => [Number(b.id), b.title])),
    achievements: new Map((achievements || []).map((a) => [Number(a.id), a.name])),
    rewards: (() => {
      const map = new Map<number, string>();
      (achievements || []).forEach((a) => {
        (a.rewards || []).forEach((r) => {
          if (r.id != null && a.name && !map.has(Number(r.id))) {
            map.set(Number(r.id), a.name);
          }
        });
      });
      return map;
    })(),
  }), [fetchedCharacters, expressions, memoryBank, nodes, achievements]);

  // Fetch nodes and characters
  // F5: Use preloadedNodes.length as dep proxy to avoid re-fetch on reference change
  const preloadedNodesLength = preloadedNodes.length;
  useEffect(() => {
    let stale = false;

    if (!enabled) {
      setNodes([]);
      setValidationCharacters([]);
      setValidationLocations([]);
      setEpisodeTextImport(null);
      setErrorMessage('');
      setCharactersReady(false);
      setToSuccess('Episode nodes loading skipped');
      return;
    }

    if (!episodeId) {
      setNodes([]);
      setEpisodeTextImport(null);
      return;
    }

    setToLoading('Loading episode nodes');
    setEpisodeTextImport(null);
    setErrorMessage('');
    setCharactersReady(false);

    // F5: Read from ref to get current value without triggering re-runs
    const currentPreloaded = preloadedNodesRef.current;
    const nodesRequest = currentPreloaded.length > 0
      ? Promise.resolve(currentPreloaded)
      : apiService.fetchStoryBranchesByEpisodeId(episodeId).then(res => res.branches);

    if (!bookId) {
      logger.info('[useEpisodeNodes] No bookId provided — characters will not be loaded');
    }

    const charactersRequest = bookId
      ? apiService.fetchCharactersByBookId(bookId)
      : Promise.resolve([]);

    Promise.all([nodesRequest, charactersRequest])
      .then(([fetchedNodes, chars]) => {
        // F10: Guard against stale fetch overwriting fresh data
        if (stale) return;
        setNodes(fetchedNodes ?? []);
        const typedChars = chars as Character[] | null;
        setFetchedCharacters(typedChars ?? []);
        setCharactersReady(true);
        const normalizedCharacters = normalizeBookCharacters(typedChars);
        setValidationCharacters(normalizedCharacters);
        setValidationLocations(buildValidationLocationsFromBranches(fetchedNodes ?? []));
        setToSuccess('Episode nodes loaded');
      })
      .catch((error) => {
        if (stale) return;
        logger.error('[useEpisodeNodes] Error in fetching episode nodes', error);
        setErrorMessage('Failed to load episode nodes');
        setToError('Failed to load episode nodes');
      });

    return () => { stale = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiService, bookId, enabled, episodeId, preloadedNodesLength, setToError, setToLoading, setToSuccess]);

  // F8: Track parse version to cancel stale async parses
  const parseVersionRef = useRef(0);

  // Parse branches into EpisodeTextImport when dictionaries or nodes change
  // F2: Wait for charactersReady before parsing to avoid Character_unknown on first render
  useEffect(() => {
    if (nodes.length === 0 || requestState !== RequestState.Success || !charactersReady) {
      return;
    }

    const currentVersion = ++parseVersionRef.current;

    setIsParsing(true);

    async function parse() {
      try {
        const parser = new BranchTextParser(nodes, dictionaries);
        const producedTsv = parser.toText();
        const { EpisodeTextImportHelper } = await import('../models/EpisodeTextImportHelper');

        // F8: Check if this parse is still current after async import
        if (parseVersionRef.current !== currentVersion) return;

        setTsvText(producedTsv);

        const parsedImport = EpisodeTextImportHelper.parseTextToEpisodeText(producedTsv);
        if (!parsedImport) {
          setErrorMessage('Could not parse episode text. No valid nodes found.');
          setEpisodeTextImport(null);
          setIsParsing(false);
          return;
        }
        setEpisodeTextImport(parsedImport);
        setIsParsing(false);
      } catch (error) {
        if (parseVersionRef.current !== currentVersion) return;
        logger.error('[useEpisodeNodes] Error parsing branches to text import', error);
        setErrorMessage('Failed to parse episode text');
        setEpisodeTextImport(null);
        setIsParsing(false);
      }
    }

    parse();
  }, [nodes, dictionaries, requestState, charactersReady]);

  return {
    nodes,
    episodeTextImport,
    tsvText,
    validationCharacters,
    validationLocations,
    requestState: requestState as RequestState,
    errorMessage,
    isParsing,
  };
}
